import React, { useEffect, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import './Derivatives.css';
import CoinInfo from '../../coinInfo/coinInfo';
import { useSelector } from 'react-redux';
import { api as axios } from '../../../api';
import url from '../../../serverUrl';
import OrderBook from './OrderBook';

function LeftPanel({ tokenData, tokenSymbol, lastBid, lastAsk, onPickPrice }) {
  const defaultToken = tokenSymbol ? tokenSymbol : 'hBTC';
  const [token, setToken] = useState(defaultToken);
  const [tokenSelected, setTokenSelected] = useState({});
  const [last10Price, setLast10Price] = useState([]);
  const [isAskChanged, setIsAskChanged] = useState(false);
  const [isBidChanged, setIsBidChanged] = useState(false);

  const { derivatives, synthetics } = useSelector((state) => state);
  const [derivativesTokens, setDerivativesTokens] = useState([]);
  useEffect(() => {
    if (derivatives && derivatives.tokens) {
      setDerivativesTokens(derivatives.tokens);
    }
  }, [derivatives]);
  useEffect(() => {
    const selcted = [...synthetics, ...derivativesTokens].find(
      (e) => e.identitySymbol === token
    );
    setTokenSelected(selcted);
  }, [synthetics, derivatives]);

  useEffect(() => {
    setTokenSelected(tokenData);
    //fetch10price(tokenData?.identitySymbol);
    if (tokenData && tokenData.token_symbol) {
      setToken(tokenData?.token_symbol);
    }
    if (tokenData && tokenData.identitySymbol) {
      setToken(tokenData?.identitySymbol);
    }
  }, [tokenData]);

  const [bidData, setBidData] = useState([]);
  const [orderData, setOrderData] = useState([]);

  const POLL_INTERVAL = 5000;
  const REQUEST_TIMEOUT = 8000;
  const MAX_RETRIES = 3;

  const isFetchingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const tokenDataRef = useRef(tokenData);
  useEffect(() => { tokenDataRef.current = tokenData; }, [tokenData]);

  // Both return Promises so the poller can track settlement
  const fetchOrder = useCallback((symbol) => {
    return axios
      .get(`${url}/hootdex/bid?symbol=${symbol}`, { timeout: REQUEST_TIMEOUT })
      .then((res) => { setOrderData(res.data); lastAsk(res.data); });
  }, [lastAsk]);

  const fetchBid = useCallback((symbol) => {
    return axios
      .get(`${url}/hootdex/order?symbol=${symbol}`, { timeout: REQUEST_TIMEOUT })
      .then((res) => { setBidData(res.data); lastBid(res.data); });
  }, [lastBid]);

  // Trigger an immediate fetch when tokenData first arrives / changes
  useEffect(() => {
    const symbolLocal =
      tokenData?.symbol || tokenData?.token_symbol || tokenData?.identitySymbol;
    if (symbolLocal) {
      fetchBid(symbolLocal).catch(() => {});
      fetchOrder(symbolLocal).catch(() => {});
    }
  }, [tokenData]);

  const runPoll = useCallback(() => {
    if (isFetchingRef.current) {
      pollTimerRef.current = setTimeout(runPoll, 1000);
      return;
    }

    const symbolLocal =
      tokenDataRef.current?.symbol ||
      tokenDataRef.current?.token_symbol ||
      tokenDataRef.current?.identitySymbol;

    if (!symbolLocal) {
      pollTimerRef.current = setTimeout(runPoll, POLL_INTERVAL);
      return;
    }

    isFetchingRef.current = true;

    Promise.all([fetchBid(symbolLocal), fetchOrder(symbolLocal)])
      .then(() => {
        retryCountRef.current = 0;
        isFetchingRef.current = false;
        pollTimerRef.current = setTimeout(runPoll, POLL_INTERVAL);
      })
      .catch((err) => {
        isFetchingRef.current = false;
        const isTimeout = err?.code === 'ECONNABORTED';
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          const backoff = Math.min(2000 * Math.pow(2, retryCountRef.current - 1), 30000);
          console.warn(`[LeftPanel] ${isTimeout ? 'timeout' : 'error'} — retry ${retryCountRef.current}/${MAX_RETRIES} in ${backoff}ms`);
          pollTimerRef.current = setTimeout(runPoll, backoff);
        } else {
          retryCountRef.current = 0;
          pollTimerRef.current = setTimeout(runPoll, POLL_INTERVAL);
        }
      });
  }, [fetchBid, fetchOrder]);

  // Start/restart poller when tokenData changes
  useEffect(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    isFetchingRef.current = false;
    retryCountRef.current = 0;
    pollTimerRef.current = setTimeout(runPoll, POLL_INTERVAL);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      isFetchingRef.current = false;
    };
  }, [tokenData, runPoll]);

  const [suggested, setSuggested] = useState([]);
  function searchHandler(searchString) {
    const filteredArray = [...derivativesTokens, ...synthetics].filter(
      function (element) {
        return (
          element.symbol
            .toLowerCase()
            .trim()
            .includes(searchString.toLowerCase()) ||
          element.identitySymbol
            .toLowerCase()
            .trim()
            .includes(searchString.toLowerCase()) ||
          element.name.toLowerCase().trim().includes(searchString.toLowerCase())
        );
      }
    );
    if (searchString !== '') {
      setSuggested(filteredArray.slice(0, 5));
    } else {
      setSuggested([]);
    }
  }

  useEffect(() => {
    setIsAskChanged(true);
    setTimeout(() => {
      setIsAskChanged(false);
    }, 2000);
  }, [orderData]);
  useEffect(() => {
    setIsBidChanged(true);
    setTimeout(() => {
      setIsBidChanged(false);
    }, 2000);
  }, [bidData]);

  // The order book itself is rendered by <OrderBook> (aggregated price
  // levels, depth bars, spread, live trades). The polling above is kept as
  // is because it feeds lastBid/lastAsk, which the order form uses for its
  // market prices - the trade path is unchanged.
  const orderSymbol =
    tokenData?.symbol || tokenData?.token_symbol || tokenData?.identitySymbol;
  const displaySymbol =
    tokenData?.identitySymbol || tokenData?.token_symbol || tokenData?.symbol;

  return (
    <Grid item xs={12} md={4} lg={3} sx={{ height: '100%', margin: '10px 0' }}>
      <OrderBook
        orderSymbol={orderSymbol}
        displaySymbol={displaySymbol}
        refPrice={tokenData?.currentPrice ?? tokenData?.token_price}
        onPickPrice={onPickPrice}
      />
    </Grid>
  );
}
export default React.memo(LeftPanel);
